<template>
  <div
    v-if="isMobile"
    id="triplabot-brr-widget-app"
    class="triplabot-brr-mobile-container"
    :style="{ backgroundColor: brrSettings.background_color }"
    @mouseover="handleHoverTrackingEvent"
  >
    <span :style="{ color: fontColor }" @click="handleDirectPriceLink">
      {{ showRegularPrice ? $t('base.membership_rate') : $t('base.direct_price') }}
      {{ formattedPrice(targetRoomPlan.sign_in_discount) }}
      <del
        >{{ $t('ota_name.' + (brrPriceList[0].ota || '').toLowerCase()) }}
        {{ brrPriceList[0].ota_price }}</del
      >
    </span>
    <button
      type="button"
      class="triplabot-brr-transparent-button triplabot-brr-mobile-close-icon"
      :style="{ fill: fontColor }"
      @click="toggleWidget"
    >
      <icon-close></icon-close>
    </button>
  </div>
  <div
    v-else
    id="triplabot-brr-widget-app"
    class="triplabot-brr-widget-container"
    :style="widgetStyle"
    @mouseover="handleHoverTrackingEvent"
  >
    <div>
      <icon-close class="triplabot-brr-widget-close-icon" @click="toggleWidget"></icon-close>
    </div>
    <div @click="handleDirectPriceLink">
      <!-- Header Section -->
      <div
        class="triplabot-brr-widget-header"
        :style="{ backgroundColor: brrSettings.background_color, color: fontColor }"
      >
        <a href="#" class="triplabot-brr-widget-link">{{ titleText }}</a>
        <div class="triplabot-brr-widget-details">{{ searchParamsText }}</div>
        <!-- Direct Price Section -->
        <div class="triplabot-brr-widget-price">
          <div class="triplabot-brr-widget-member-price-row">
            <div class="triplabot-brr-widget-price-label" :style="{ color: brrSettings.text_color }">
              <strong>{{
                showRegularPrice ? $t('base.membership_rate') : $t('base.direct_price')
              }}</strong>
            </div>
            <div class="triplabot-brr-widget-price-value" :style="{ color: brrSettings.text_color }">
              {{ formattedPrice(targetRoomPlan && targetRoomPlan.sign_in_discount) }}
            </div>
          </div>
          <div v-if="showRegularPrice" class="triplabot-brr-widget-regular-price-row">
            <div class="triplabot-brr-widget-price-label" :style="{ color: brrSettings.text_color }">
              <span>{{ $t('base.regular_rate') }}</span>
            </div>
            <del class="triplabot-brr-widget-price-value" :style="{ color: brrSettings.text_color }">
              {{ formattedPrice(targetRoomPlan && targetRoomPlan.non_sign_in_discount) }}
            </del>
          </div>
        </div>
      </div>

      <!-- Competitor Prices Section -->
      <div class="triplabot-brr-widget-competitor-prices">
        <div
          v-for="(price, index) in brrPriceList"
          :key="index"
          class="triplabot-brr-widget-price-row"
        >
          <div>
            {{ $t('ota_name.' + (price.ota || '').toLowerCase()) }}
          </div>
          <div>{{ price.ota_price }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isMobile, priceWithComma } from 'Utilities'
import IconClose from 'assets/icons/close.svg?inline'
import { useAppStore } from 'Store/app'
import { useSettingsStore } from 'Store/settings'
import findFontColor from 'Utilities/findFontColor'
import { CURRENCY_SYMBOL_MAP } from 'javascript/Constants'
import { brrDateDisplay } from 'Utilities/TimerHelper'
export default {
  components: {
    IconClose
  },
  setup() {
    const appStore = useAppStore()
    const settingsStore = useSettingsStore()
    return {
      isMobile: isMobile(),
      appStore,
      settingsStore,
      hoverSent: false
    }
  },
  props: {
    zIndex: {
      type: Number,
      default: 10
    },
    bottomPosition: {
      type: String,
      default: ''
    },
    rightPosition: {
      type: String,
      default: ''
    },
    leftPosition: {
      type: String,
      default: ''
    },
    brrSettings: {
      type: Object,
      default: () => {}
    },
    brrPriceList: {
      type: Array,
      default: () => []
    },
    targetRoomPlan: {
      type: Object,
      default: () => null
    },
    searchParams: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    fontColor() {
      return findFontColor(this.brrSettings.background_color)
    },
    showRegularPrice() {
      return (
        this.findTotalPrice(this.targetRoomPlan.non_sign_in_discount) >
        this.findTotalPrice(this.targetRoomPlan.sign_in_discount)
      )
    },
    widgetStyle() {
      return {
        display: 'block',
        position: 'fixed',
        bottom: this.bottomPosition,
        right: this.rightPosition,
        left: this.leftPosition,
        transform: 'none',
        visibility: 'visible',
        zIndex: this.zIndex,
        transition: 'all 0.3s',
        background: '#fff',
        borderRadius: '10px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        width: '250px',
        fontFamily: 'Arial, sans-serif',
        color: this.brrSettings.text_color
      }
    },
    titleText() {
      const matchingTranslationObject = this.brrSettings.translations_attributes.find(
        (translation) => translation.locale === this.$i18n.locale && translation.locale_enabled
      )
      return matchingTranslationObject ? matchingTranslationObject.title : this.$t('base.book')
    },
    searchParamsText() {
      return brrDateDisplay(
        this.searchParams.adults || 2,
        this.searchParams.checkIn,
        this.searchParams.checkOut
      )
    },
    trackingPayload() {
      return {
        room_plan_code: this.targetRoomPlan.room_plan_code,
        hotel_brand_id: this.settingsStore.getBrandId,
        search_params: this.searchParamsText,
        device: this.isMobile ? 'mobile' : 'desktop',
        room_price: this.formattedPrice(this.targetRoomPlan.sign_in_discount),
        is_on_top_bw_iframe: this.appStore.isOnBookingWidget,
        ota_price: this.brrPriceList
          .filter((price) => price.ota_price_value > 0)
          .map(
            (price) =>
              `${this.$t('ota_name.' + (price.ota || '').toLowerCase())} - ${price.ota_price}`
          )
          .join(', ')
      }
    }
  },
  watch: {
    searchParams(newSearchParams, oldSearchParams) {
      if (
        !newSearchParams.otaPriceFetching &&
        newSearchParams.otaPriceFetching !== oldSearchParams.otaPriceFetching
      ) {
        this.$gtag.trackGA4Event('brr_widget_impression', this.trackingPayload)
        this.hoverSent = false
      }
    }
  },
  mounted() {
    // this.$gb.isOn('brr-widget') // just to trigger experiment
    // this.$gb.getFeatureValue('brr-widget', 'D1') // just to trigger experiment
    this.$gtag.trackGA4Event('brr_widget_display', this.trackingPayload)
  },
  methods: {
    findTotalPrice(priceObject) {
      return (
        priceObject?.total_price_after_discount +
        priceObject?.total_price_after_discount_tax +
        priceObject?.total_accommodation_tax
      )
    },
    formattedPrice(priceObject) {
      if (!priceObject) return 'N/A'
      const symbol = CURRENCY_SYMBOL_MAP[this.settingsStore.settings.currency] || '¥'
      const finalPrice = this.findTotalPrice(priceObject)
      return symbol + priceWithComma(finalPrice)
    },
    toggleWidget() {
      this.$gtag.trackGA4Event('brr_widget_close', this.trackingPayload)
      this.$emit('close')
    },
    handleDirectPriceLink() {
      this.handleClickTrackingEvent()
      this.$emit('open-bw')
    },
    handleClickTrackingEvent() {
      this.$gtag.trackGA4Event('brr_widget_click', this.trackingPayload)
    },
    handleHoverTrackingEvent() {
      if (!this.hoverSent) {
        this.$gtag.trackGA4Event('brr_widget_hover', this.trackingPayload)
      }
      this.hoverSent = true
    }
  }
}
</script>

<style lang="scss" scoped>
.triplabot-brr-transparent-button {
  background: rgba(0, 0, 0, 0);
  border: none;
  padding: 0;
}

.triplabot-brr-mobile-container {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  color: #fff;
  padding: 8px 12px;
  border-radius: 10px 10px 0 0;
  font-weight: 600;
  font-family: 'Arial', system-ui;
  box-sizing: border-box;
  font-size: 14px;

  del {
    font-weight: 400;
    margin-left: 4px;
    font-size: 14px;
  }

  .triplabot-brr-mobile-close-icon {
    float: right;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
}

.triplabot-brr-widget-header {
  background-color: #d71d81;
  color: #fff;
  padding: 10px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  position: relative;
  text-align: center;
}

.triplabot-brr-widget-link {
  font-weight: bold;
  color: #fff;
  text-decoration: none;
  font-size: 14px;
}

.triplabot-brr-widget-details {
  font-size: 12px;
  margin-top: 4px;
}

.triplabot-brr-widget-button-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  z-index: 99;
}

.triplabot-brr-widget-close-icon {
  width: 16px;
  height: 16px;
  position: absolute;
  cursor: pointer;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  right: -5px;
  top: -5px;
  border-radius: 50%;
  z-index: 1;
  padding: 2px;
}

.triplabot-brr-widget-expand-icon {
  position: absolute;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  right: 0px;
  top: 0px;
  border-radius: 50%;
  fill: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.triplabot-brr-widget-price {
  cursor: pointer;
  margin-top: 5px;
  background-color: #f8f8f8;
  padding: 10px;
  border-radius: 8px;
  color: #000;
}

.triplabot-brr-widget-member-price-row,
.triplabot-brr-widget-regular-price-row {
  display: flex;
  justify-content: space-between;
}

.triplabot-brr-widget-member-price-row {
  .triplabot-brr-widget-price-label {
    font-size: 14px;
    font-weight: bold;
  }

  .triplabot-brr-widget-price-value {
    font-weight: bold;
    color: #000;
    font-size: 18px;
  }
}

.triplabot-brr-widget-regular-price-row {
  .triplabot-brr-widget-price-label {
    font-size: 13px;
    font-weight: 400;
  }

  .triplabot-brr-widget-price-value {
    font-size: 13px;
  }
}

.triplabot-brr-widget-compare-text {
  margin-top: 5px;
  font-size: 12px;
}

.triplabot-brr-widget-competitor-prices {
  padding: 10px;
}

.triplabot-brr-widget-price-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 14px;
}

.triplabot-brr-widget-container {
  cursor: pointer;
}
</style>
